import { autorun, makeAutoObservable, runInAction } from 'mobx';
import { EE, HelperCore } from '@/common/dependencies';
import navbarStore from '../navbar';
import { request } from '@/common';
import Loader from '@/common/helper/loader';
import { getAttachmentsId, getMcpToolsConfig, isAgentFlowId, McpActionTools, McpActionTypes, McpAgentType, McpFilesConfig, McpToolsConfig } from '@shared/constants/mcp';
import InviteCodeModal from '@/components/modal/invite-code';
import { INVITE_STATUS_ACTIVE } from '@/components/modal/invite-form/constant';
import copy from 'copy-to-clipboard';
import Other from '@/common/helper/other';
import globalStore from '../global';
import slsLogger from '@public/log/sls';
import client from '../client';
import createDebug from '@/common/create-debug';

const debugMcp = createDebug("mcp");
const DEFAULT_REPLY_TIME = 150;

class McpStore {
  chatStore = null;
  showRightPanel = false;         // 是否显示右侧屏幕
  panelMessageId = null;          // 右侧当前显示哪条消息的步骤
  showSteps = false;              // 是否展开步骤
  agentId = null;                 // 智能体 ID
  agentInfo = null;               // 智能体详情
  allSteps = [];                  // 所有一级节点

  currentPanelId = null;          // 右侧当前显示的步骤 ID(三级工具 ID)
  hasSelectedPanelId = false;     // 用户是否手动选择了步骤
  hasAutoShowRightPanel = false;  // 是否已经自动打开过右侧

  currentFileNode = null;         // 右侧文件
  isHtmlPreview = false;          // Html 为预览状态
  htmlTheme = "light";            // Html 主题

  agentInfoPromise = null;        // 等待加载 agentInfo
  agentInfoResolve = null;

  previewImages = [];             // 当前消息中的所有图片，用于预览大图

  genHtmlStatus = new Map();           // 生成 html 状态 0 未生成 1 生成中 2 已生成
  taskSearchObj = {};             // 多任务搜索

  /**
   * 添加了 Mcp Server 的智能体，走任务流程
   * 没有添加 Mcp Server 的智能体，走模型(R1)对话
   */
  get isMcpAgent () {
    try{
      return [McpAgentType.normal, McpAgentType.simple].includes(this.agentInfo.agent_type);
      // return !!this.agentInfo.server_list.length;
      return
    } catch(e){}
    return false;
  }
  // 当前智能体，是否为简版
  get isSimpleAgent () {
    return this.agentInfo?.agent_type == McpAgentType.simple;
  }
  // 当前智能体，是否为工作流
  get isAgentFlow () {
    return this.agentId && this.agentInfo && isAgentFlowId(this.agentId);
  }
  /**
   * 新版智能体，不论是否有 Mcp Server
   */
  get isAgent () {
    return !!this.agentId;
  }

  // 是否为mcp server
  get isMcpServer() {
    return this.chatStore.lastMessage?.cloud_mcp?.length > 0 || this.chatStore.lastMessage?.mcp?.length > 0;
  }

  // 组织 events 结构
  displayEventsCache = {};
  get displayEvents () {
    if(!this.panelMessageId || !this.chatStore){
      return [];
    }
    const messageItem = this.chatStore.messageMap.get(this.panelMessageId)
      || this.chatStore.messageList.find(item => item.msg_id == this.panelMessageId);
    if(!messageItem?.events?.length){
      return [];
    }

    const events = messageItem.events;
    // if(messageItem.msg_id){
    //   if(!this.displayEventsCache[messageItem.msg_id]){
    //     const displayEvents = this.getFixedEvents(events);
    //     this.displayEventsCache[messageItem.msg_id] = {
    //       eventsLen : events.length,
    //       displayEvents,
    //     }
    //   }
    //   if(this.displayEventsCache[messageItem.msg_id].eventsLen == events.length){
    //     return this.displayEventsCache[messageItem.msg_id].displayEvents;
    //   }
    // }

    // debug
    // HelperCore.log(`call getFixedEvents by displayEvents`, events.length);

    const displayEvents = this.getFixedEvents(events);
    return displayEvents;
  }

  // 当前正在输出的一级 ID(最后一个一级节点)
  get currentTypingId () {
    if(!this.steps.length){
      return null;
    }
    return this.steps[this.steps.length - 1].planStepId;
  }

  // 当前正在输出的一级节点
  get currentTypingNode () {
    if(!this.currentTypingId){
      // TODO 有一级节点，没有 planStepId 的情况，模型修复后可删除
      if(this.steps.length){
        return this.steps[this.stepCount - 1];
      }

      return null;
    }
    return this.steps.find(item => item.planStepId == this.currentTypingId);
  }

  // 右侧当前显示的步骤
  get currentPaneNode () {
    // debug
    // return null;

    if(!this.currentPanelId){
      return null;
    }
    // 简版，不展示小电脑
    if(this.isSimpleAgent && !globalStore.globalConfig.supportPanelWithSimpleAgent){
      return null;
    }

    const node = this.toolsNodes.find(item => item.planStepId == this.currentPanelId && item.msg_id == this.panelMessageId);
    if(!node){
      return null;
    }
    return node.event;
  }

  // 所有已输出的一级节点
  get steps () {
    const steps = this.displayEvents.find(item => item.type == "steps");
    if(!steps){
      return [];
    }
    return steps.list;

    // if(this.chatStore.isAITyping){
    //   const steps = this.displayEvents.find(item => item.type == "steps");
    //   if(!steps){
    //     return [];
    //   }
    //   return steps.list;
    // }
    // // 结束时返回所有，掩盖模型中断
    // return this.allSteps;
  }

  get stepCount () {
    return this.allSteps.length;
  }

  get currentStepIndex () {
    if(this.chatStore.isAITyping){
      return Math.max(0, this.steps.findIndex(item => item.planStepId == this.currentTypingId));
    }
    // 结束时返回所有，掩盖模型中断
    return this.stepCount - 1;
  }

  // 所有三级节点，可用于右侧显示的节点
  get toolsNodes () {
    const list = [];

    // debug
    // debugMcp("toolsNodes");

    // 会话的所有消息
    const messageList = this.chatStore.messageList;
    if(!messageList?.length){
      return [];
    }
    messageList.forEach(message => {
      const { events, msg_id } = message;
      if(!events?.length){
        return;
      }
      const level1Events = [];
      events.forEach(event => {
        if(this.isInvalidEvent(event)){
          return;
        }

        if(event.type == "planStep"){
          level1Events.push(event);
        }

        if(event.tool === 'browser_automation_multiple_task' || event.tool === 'browser_automation_addcart_task'){
          this.replaceEvent(list, this.getFixedPanelParams({
            msg_id,
            // ...event,
            ...this.taskSearchObj[event.planStepId],
          }));
        }
        else if(
          (event.tool && McpToolsConfig[event.tool])
          || McpActionTypes.includes(event.type)
          || (
            ["toolNeedConfirm", "extraData"].includes(event.type)
            && (
              level1Events.length != 1
              || event.status != "running"
            )
          )
          || ["params"].includes(getMcpToolsConfig(event).key)
          || McpActionTools.includes(event.tool)
        ){
          this.replaceEvent(list, this.getFixedPanelParams({
            msg_id,
            ...event,
          }));
        }

        // if(event.type == "attachments"){
        //   (event.attachments || []).forEach(attachment => {
        //     // 忽略过程中生成的节点
        //     if(attachment.planStepId){
        //       return;
        //     }
        //     const { contentType } = attachment;
        //     const config = McpFilesConfig[contentType];
        //     if(!config){
        //       return;
        //     }
        //     if(config.supportPreview){
        //       const params = {
        //         tool : config.previewTool || contentType,
        //         msg_id,
        //         planStepId : getAttachmentsId(attachment),
        //         event : {
        //           msg_id,
        //           planStepId : getAttachmentsId(attachment),
        //           ...attachment,
        //         },
        //       }
        //       if(config.previewKey){
        //         params.data = attachment[config.previewKey];
        //       }
        //       else {
        //         params.data = attachment.content;
        //         Object.assign(params, attachment);
        //       }
        //       list.push(params);
        //     }
        //   });
        // }

        // 旧版本 html 兼容
        if(event.type == "html"){
          const params = {
            tool : "html",
            msg_id,
            planStepId : getAttachmentsId(event),
            event : {
              msg_id,
              planStepId : getAttachmentsId(event),
              status : event.content && /\/html\>\s*/i.test(event.content) ? "success" : "running",
              ...event,
            },
            data : event.content,
            url : event.url,
          }
          list.push(params);
        }
      });
    });

    return list
      // 过滤已结束的 think 节点
      .filter(item => {
        const event = item.event;
        if(!event){
          return true;
        }
        if(["planStatus", "planThink"].includes(event.type) && ["success", "failed"].includes(event.status) && globalStore.globalConfig.hideFinishedThink){
          return false;
        }
        return true;
      })
  }

  // 所有附件节点，用于左侧下方列表点击预览使用
  get toolsAttachmentsNodes () {
    const list = [];

    // 会话的所有消息
    const messageList = this.chatStore.messageList;
    if(!messageList?.length){
      return [];
    }
    messageList.forEach(message => {
      const { events, msg_id } = message;
      if(!events?.length){
        return;
      }
      events.forEach(event => {
        if(this.isInvalidEvent(event)){
          return;
        }

        if(event.type == "attachments"){
          (event.attachments || []).forEach(attachment => {
            // 忽略过程中生成的节点
            if(attachment.planStepId){
              return;
            }
            const { contentType } = attachment;
            const config = McpFilesConfig[contentType];
            if(!config){
              return;
            }
            if(config.supportPreview){
              const params = {
                tool : config.previewTool || contentType,
                msg_id,
                planStepId : getAttachmentsId(attachment),
                event : {
                  msg_id,
                  planStepId : getAttachmentsId(attachment),
                  ...attachment,
                },
              }
              if(config.previewKey){
                params.data = attachment[config.previewKey];
              }
              else {
                params.data = attachment.content;
                Object.assign(params, attachment);
              }
              list.push(params);
            }
          });
        }
      });
    });

    return list;
  }

  get isAborted () {
    return this.steps.some(item => item.sender == "system")
      || this.displayEvents.some(item => item.sender == "system")
  }

  get isRightOpen () {
    return this.showRightPanel || this.currentFileNode;
  }

  // 是否 HTML 消息，并正在输出 html
  get isTypingHtml () {
    if(this.isAgent || this.isMcpServer){
      try{
        if(!this.chatStore.isAITyping){
          return false;
        }
        const htmlEvent = this.displayEvents.find(item => item.type == "html");
        if(!htmlEvent){
          return false;
        }
        return htmlEvent.content && !/\/html\>\s*/i.test(htmlEvent.content);
      } catch(e){}
      return false;
    }

    try{
      if(!this.chatStore.isAITyping){
        return false;
      }
      if(!this.chatStore.lastMessage.code_info.title){
        return false;
      }
      return this.chatStore.lastMessage.result.split('```').length == 2;
    } catch(e){}
    return false;
  }
  setGeneraHtmlStatus(status, html_message_id) {
    runInAction(() => {
      this.genHtmlStatus.set(html_message_id, status);
    })
  }
  // 是否 HTML 消息，并且当前右侧的 html 是完整的
  get hasHtmlFinished () {
    if(this.isAgent || this.isMcpServer){
      try{
        if(!this.currentFileNode){
          return false;
        }
        if(this.currentFileNode.type != "html"){
          return false;
        }
        const htmlEvent = this.currentFileNode;
        return htmlEvent.content && /\/html\>\s*/i.test(htmlEvent.content);
      } catch(e){}
      return false;
    }

    try{
      if(!this.currentFileNode){
        return false;
      }
      const messageItem = this.chatStore.messageList.find(item => item.msg_id == this.currentFileNode.messageId);
      if(!messageItem){
        return false;
      }
      if(!messageItem.code_info?.title){
        return false;
      }
      return messageItem.result.split('```').length >= 3;
    } catch(e){}
    return false;
  }

  // 当前 file 节点，是否 html 节点
  get currentFileIsHtmlNode () {
    if(!this.currentFileNode){
      return false;
    }
    return this.currentFileNode.type == "v3"            // V3 html 预览
      || this.currentFileNode.contentType == "html"     // 智能体 html 总结
      || this.currentFileNode.type == "html"            // 智能体 html 节点
  }

  /**
   * FileViewer 数据格式化
   */
  get fixedFileNode () {
    if(!this.currentFileNode){
      return null;
    }
    const currentFileNode = HelperCore.copyObj(this.currentFileNode);
    if(currentFileNode.contentType == "pdf"){
      return {
        ...currentFileNode,
        ...{
          type : "pdf",
        },
      };
    }

    // html 节点
    if(this.currentFileIsHtmlNode){
      return currentFileNode;
    }

    const event = this.toolsNodes.find(item => item.planStepId == this.currentFileNode.planStepId);
    if(!event){
      if(this.currentFileNode.code){
        return {
          ...this.currentFileNode,
          ...{
            type : "execute",
            content : '```' + (this.currentFileNode.language || "") + '\n' + this.currentFileNode.code + '\n```',
          }
        }
      }

      return {
        ...this.currentFileNode,
        ...{
          type : this.currentFileNode.contentType,
          content : (
            [
              "image",
              "video",
              "audio",
            ].includes(this.currentFileNode.contentType)
            ? this.currentFileNode.url
            // pdf/md/html
            : (
              this.currentFileNode.content
              || ""
            )
          ),
          url : (this.currentFileNode.url || "").replace(/^http\:/, 'https:'),
        }
      }
    }

    const vncParams = this.getFixedPanelParams(event);
    return {
      ...vncParams,
      ...{
        type : vncParams.tool,
        content : vncParams.data,
      },
    };
  }

  // 智能体类型已发生变化
  get agentTypeNotMatch () {
    if(!this.agentInfo || !this.chatStore){
      return false;
    }
    const {
      agent_type : currentType,
    } = this.agentInfo;

    const {
      historyData,
    } = this.chatStore;
    const oldType = historyData?.ai_agent_type;

    // 历史数据，服务端上线后考虑去掉
    if(oldType == undefined){
      return false;
    }

    if(currentType != oldType){
      return true;
    }

    return false;
  }

  get agentStatusFailed () {
    // debug
    // return true;

    const agentStatusEvent = this.displayEvents.find(item => item.type == "agentStatus");
    if(agentStatusEvent?.success === false){
      return true;
    }
    return false;
  }

  /**
   *
   */
  get taskSearchList() {
    if(!this?.panelMessageId || !this.chatStore){
      return [];
    }
    const list = [];
    for (const o in this.taskSearchObj) {
      const {
        taskName,
        taskId,
        query,
        image,
        position,
      } = this.taskSearchObj[o];
      list.push({
        taskName,
        taskId,
        query,
        image,
        position,
      })
    }
    return list;
  }

  /**
   * 正在等待补充信息
   */
  get isNeedConfirm () {
    try{
      const lastMessage = this.chatStore.lastMessage;
      const events = lastMessage.events.filter(event => event.type == "toolNeedConfirm");
      let eventStatus = {};
      events.forEach(event => {
        const { planStepId, status } = event;
        eventStatus[planStepId] = status;
      });
      return events.length && Object.values(eventStatus).includes("running");
    } catch(e){}
    return false;
  }

  constructor (chatStore) {
    this.chatStore = chatStore;
    this.agentInfoPromise = new Promise((resolve) => {
      runInAction(() => {
        this.agentInfoResolve = resolve;
      });
    });

    makeAutoObservable(this, {
      previewImages : false,
      taskSearchObj : false,
    });

    autorun(() => {
      if(this.isTypingHtml && !this.isAgent){
        runInAction(() => {
          this.isHtmlPreview = false;
          this.setCurrentFileNode({
            content : "",
            type : "v3",
            title : this.chatStore.lastMessage.code_info.title,
            messageId : this.chatStore.lastMessage.msg_id,
          })
        })
      }
    });

    // debug
    // autorun(() => {
    //   if(this.isHtmlPreview){
    //     console.warn("mcpStore isHtmlPreview true");
    //   }
    // })

    EE.on("AIAgentEdit", agentInfo => {
      if(agentInfo.id && agentInfo.id == this.agentId){
        this.updateAgentInfo();
      }
    })
  }

  /**
   * 右侧是否展示
   * @param {Boolean} showRightPanel
   */
  setShowRightPanel (showRightPanel) {
    this.showRightPanel = showRightPanel;
    if(showRightPanel){
      // navbarStore.setIsCollapse(true);
    }
    if(showRightPanel){
      this.setCurrentFileNode(null);
    }
  }
  toggleShowRightPanel = () => {
    this.setShowRightPanel(!this.showRightPanel);
  }

  /**
   * 是否展开步骤
   * @param {Boolean} showSteps
   */
  setShowSteps (showSteps) {
    this.showSteps = showSteps;
  }
  toggleShowSteps = () => {
    this.setShowSteps(!this.showSteps);
  }

  /**
   * 是否当前正在输出的一级 ID
   * @param {Object} node
   * @returns
   */
  isCurrentNode (node) {
    if(!node){
      return false;
    }
    const {
      id,
    } = node;
    return id == this.currentTypingId;
  }

  /**
   * 设置智能体信息
   * @param {String} agentId
   * @param {?Object} agentInfo
   */
  setAgentID (agentId, agentInfo) {
    this.agentId = agentId;

    if(agentInfo){
      this.agentInfo = agentInfo;
      return;
    }

    if(isAgentFlowId(agentId)){
      request.get("/ai_flow/flow_info", {
        params : {
          flow_id : agentId,
        },
      })
      .then(res => {
        const { data } = res;

        if(!data) {
          Other.toast("该智能体已被下架", "warning")
          setTimeout(() => {
            globalStore.hrefNavigate(HelperCore.repaireUrl("/tools/aiagent"));
          }, 2000)
          return
        }

        runInAction(() => {
          this.agentInfo = data;
          if(this.agentInfoResolve){
            this.agentInfoResolve();
          }
        });
      })
      .catch((err) => {
        if(err.code == 500) {
          Other.toast("智能体已被下架", "warning")
          setTimeout(() => {
            globalStore.hrefNavigate(HelperCore.repaireUrl("/tools/aiagent"));
          }, 2000)
          return
        }
        Loader.error(err)
      });
      return;
    }

    request.get("/ai_agent/detail", {
      params : {
        id : agentId,
      },
    })
    .then(res => {
      const { data } = res;

      if(!data) {
        Other.toast("该智能体已被下架", "warning")
        setTimeout(() => {
          globalStore.hrefNavigate(HelperCore.repaireUrl("/tools/aiagent"));
        }, 2000)
        return
      }

      // debug 自带默认 prompt
      // if(HelperCore.IS_LOCAL){
      //   data.doc_def_prompt = "总结一下";
      // }

      runInAction(() => {
        this.agentInfo = data;
        if(this.agentInfoResolve){
          this.agentInfoResolve();
        }
      });
    })
    .catch((err) => {
      if(err.code == 500) {
        Other.toast("智能体已被下架", "warning")
        setTimeout(() => {
          globalStore.hrefNavigate(HelperCore.repaireUrl("/tools/aiagent"));
        }, 2000)
        return
      }
      Loader.error(err)
    });
  }
  updateAgentInfo = () => {
    const agentInfo = HelperCore.copyObj(this.agentInfo);

    request.get("/ai_agent/detail", {
      params : {
        id : this.agentId,
      },
    })
    .then(res => {
      const { data } = res;

      runInAction(() => {
        this.agentInfo = data;
      });
    })
    .catch(() => {})
    .finally(() => {
      slsLogger.log({
        action : "agentInfoUpdate",
        ext1 : JSON.stringify(this.agentInfo),
        ext2 : JSON.stringify(agentInfo),
      });
    })
  }

  /**
   * 右侧 切换消息
   * @param {String} panelMessageId
   */
  setPanelMessageId (panelMessageId) {
    if(this.panelMessageId == panelMessageId){
      return;
    }
    this.panelMessageId = panelMessageId;
  }

  /**
   * 组织 events 层级
   * @param {Array} events
   */
  getFixedEvents (events) {
    if(!events?.length){
      this.previewImages = [];
      return [];
    }

    // debug
    // debugMcp("getFixedEvents");

    // debug
    // console.warn(`run getFixedEvents`);

    let previewImages = [];
    let stepsList = null; // 大纲列表
    try{
      events = events.length > 50 && window.structuredClone
      ? window.structuredClone(events)
      : HelperCore.copyObj(events);
    } catch(e){
      events = HelperCore.copyObj(events);
    }
    const list = [];
    let lastLevel1Event = null;
    let level1Events = [];
    let level2Events = [];
    let level3Events = [];
    const level3Children = {};
    events.forEach(event => {
      const {
        type,
      } = event;

      if(this.isFilteredEvent(event)){
        return;
      }

      switch(type){
        // 一级
        case "planStep":{
          let steps = list.find(item => item.type == "steps");
          if(!steps){
            steps = {
              type : "steps",
              list : [],
            };
            list.push(steps);
          }
          level1Events = level1Events.filter(item => item.planStepId != event.planStepId);

          // debug
          // if(globalStore.debugSimpleMcp){
          //   event.hideTitle = true;
          // }

          event = {
            ...event,
            displayIndex : level1Events.length,
          }

          // 强制 TODO，模型如果能改就模型改
          // if(this.isAgentFlow){
          //   event.hideTitle = true;
          // }

          // 工作流，都放在一个一级节点下
          if(this.isAgentFlow && lastLevel1Event){
            break;
          }

          this.replaceEvent(level1Events, event);
          this.replaceEvent(steps.list, event);
          lastLevel1Event = event;
          break;
        }
        // 二级
        case "subPlanStep":{
          let level1Event = lastLevel1Event;
          if(!level1Event){
            return;
          }
          level1Event.children = level1Event.children || [];
          level1Event.children = level1Event.children.filter(item => item.planStepId != event.planStepId);

          // debug
          // if(globalStore.debugSimpleMcp){
          //   event.title = "";
          //   event.titleCN = "";
          //   event.description = "";
          // }

          // 强制 TODO，模型如果能改就模型改
          // if(this.isAgentFlow){
          //   event.title = "";
          //   event.titleCN = "";
          //   event.description = "";
          // }

          // 工作流，都放在一个二级节点下
          if(this.isAgentFlow && level1Event.children.length){
            break;
          }

          level1Event.children.push(event);
          break;
        }
        // 三级
        case "planStatus":
        case "planThink":
        case "toolStatus":
        case "agentBlock":
        case "agentInteraction":
        // case "planUpdate":
        case "queryList":
        case "toolNeedConfirm":
        case "extraData":
        {
          let level1Event = lastLevel1Event;
          // 工作流，按 mcp 合并？会跳
          // if(this.isAgentFlow){
          //   const {
          //     idx,
          //     parentStepId,
          //   } = event;
          //   let steps = list.find(item => item.type == "steps");
          //   if(steps?.list?.length){
          //     level1Event = steps.list.find(item => {
          //       return item.idx == idx
          //         && item.children?.length
          //         && item.children.some(item => item.planStepId == parentStepId);
          //     })
          //   }
          // }

          if(!level1Event){
            return;
          }
          // 取所有一级节点
          if(type == "planUpdate" && event.planStepId == "sub_step_data"){
            try{
              const list = JSON.parse(event.detail.steps.content);
              runInAction(() => {
                this.allSteps = list;
              });
            } catch(e){}
            return;
          }

          if(!level1Event?.children?.length){
            return;
          }
          const level2Event = level1Event.children.find(item => item.planStepId == event.parentStepId) || level1Event.children[level1Event.children.length - 1];
          if(!level2Event){
            // debug
            console.error(`level2Event notfound`, event, list);
            return;
          }
          if(this.isInvalidEvent(event)){
            return;
          }

          level2Event.children = level2Event.children || [];
          // level2Event.children = level2Event.children.filter(item => item.planStepId != event.planStepId);
          // level2Event.children.push(event);
          const index = level2Event.children.findIndex(item => item.planStepId == event.planStepId);
          if(level3Children[event.planStepId] && Array.isArray(level3Children[event.planStepId])){
            event.children = level3Children[event.planStepId];
          }
          if(index >= 0){
            // task_search，需要复制children
            // if ((event.buType === 'task_search' || event.buType === 'task_add_to_cart') && level2Event.children[index].children) {
            //   event.children = level2Event.children[index].children;
            // }
            if (event.buTaskId && event.type === 'toolStatus' && (event.tool === 'browser_automation_multiple_task' || event.tool === 'browser_automation_addcart_task')) {
              try{
                const targetEvent = (this.taskSearchObj[event.planStepId]);
                if(!targetEvent){
                  return;
                }
                const lastResults = targetEvent.detail.results;
                const currentResult = lastResults.find(item => item.buTaskId == event.buTaskId);
                if(!currentResult){
                  HelperCore.log(`多页面，未找到对应的 task`, event.planStepId, event.buTaskId, event);
                  return;
                }
                if(event.is_pic_frame){
                  currentResult.imageData = event.detail.results[0];
                }
                else if(event.buResult){
                  currentResult.actions = currentResult.actions || [];
                  currentResult.actions.push(event.buResult);
                }
                currentResult.status = event.status;

                // 当所有 task 结束时，节点更新为结束状态
                if(lastResults.every(result => result.status == "success")){
                  level2Event.children[index].status = "success";
                }
              } catch(e){
                HelperCore.log("多页面，合并多页面搜索数据报错:", event, e);
              }
              return;
            }
            level2Event.children.splice(index, 1, this.mergeEvent(event, level2Event.children[index]));
          }
          else {
            level2Event.children.push(event);
          }

          level3Events = level3Events.filter(_ => _.planStepId != event.planStepId);
          level3Events.push(event);
          if(event.tool === 'browser_automation_multiple_task' || event.tool === 'browser_automation_addcart_task'){
            runInAction(() => {
              this.taskSearchObj[event.planStepId] = HelperCore.copyObj(event);
            });
          }

          // 购物mcp时，放在右侧2x2小电脑中的图片
          // if (event.buTaskId && event.type === 'toolStatus' && (event.tool === 'browser_automation_multiple_task' || event.tool === 'browser_automation_addcart_task')) {
          //   const {
          //     contentType,
          //     url,
          //     position,
          //   } = event?.detail?.results?.[0] || {};
          //   if (contentType === 'image' && url && this.taskSearchObj[event.buTaskId]) {
          //     runInAction(() => {
          //       this.taskSearchObj[event.buTaskId].image = url;
          //       this.taskSearchObj[event.buTaskId].position = position;
          //     });
          //   }
          // }

          //按buTaskId把行为数据task_status进行聚合
          level2Event?.children.forEach(level3Event => {
            if (
              (level3Event.tool === 'browser_task_search' && level3Event.buType === 'task_search') ||
              (level3Event.tool === 'browser_task_search' && level3Event.buType === 'task_add_to_cart')
            ) {
              level3Event.children = level3Event.children || [];
              if (!this.taskSearchObj[level3Event.buTaskId]) {
                runInAction(() => {
                  this.taskSearchObj[level3Event.buTaskId] = {
                    taskName: level3Event?.extra_parameters?.buTaskName,
                    taskId: level3Event.buTaskId,
                    query: level3Event.buResult?.result?.content,
                    image: '',
                  }
                });
              }

              if (event.buType === 'task_status' && level3Event.buTaskId === event.buTaskId) {
                level3Event.children.push(event)
              }
            }
          })

          // 图片节点
          const image = this.getImageFromEvent(event);
          if(image){
            previewImages.push({
              image,
              event,
            });
          }
          break;
        }
        // 四级节点
        case "knowledgeStatus":{
          const level3Event = level3Events.find(item => item.planStepId == event.parentStepId);
          // 出现过没有对应的 3 级节点的情况
          if(!level3Event){
            break;
          }
          level3Event.children = level3Event.children || [];
          const index = level3Event.children.findIndex(item => item.planStepId == event.planStepId);
          if(index >= 0){
            level3Event.children.splice(index, 1, this.mergeEvent(event, level3Event.children[index]));
          }
          else {
            level3Event.children.push(event);
          }
          level3Children[event.parentStepId] = level3Event.children;
          break;
        }

        // html 节点
        case "html":
        // Markdown 节点
        case "chat":
        // 追问节点
        case "clarification":
        // 最后总结
        case "agentStatus":
        case "convertFinalSummary":
        // 小红书笔记
        case "notes":
        // 小红书视频
        case "media":
        // 导购
        case "recommended_goods": // 标识是否添加过购物车
        // 消耗
        case "taskDetail":
        {
          let steps = list.find(item => item.type == "steps");
          if(!steps){
            steps = {
              type : "steps",
              list : [],
            };
            list.push(steps);
          }
          this.replaceEvent(steps.list, event);
          break;
        }
        // 附件节点
        case "attachments":{
          let steps = list.find(item => item.type == "steps");
          if(!steps){
            steps = {
              type : "steps",
              list : [],
            };
            list.push(steps);
          }
          // 过滤掉无效数据
          let attachments = (event.attachments || []).filter(attachment => {
            // 没有 url 的
            // if(!attachment.url){
            //   return false;
            // }
            // 没有 url 的 pdf
            if(attachment.contentType == "pdf" && !attachment.url){
              return false;
            }
            // 没有标题
            // if(!attachment.title){
            //   return false;
            // }
            return true;
          });
          attachments = attachments.map(attachment => {
            return {
              planStepId : getAttachmentsId(attachment),
              ...attachment,
            }
          })
          event.attachments = attachments;

          // debug video
          // if(HelperCore.IS_LOCAL){
          //   event.attachments = event.attachments || [];
          //   if(!event.attachments.some(item => item.contentType == "video")){
          //     event.attachments.unshift({
          //       contentType : "video",
          //       content : "",
          //       url : "https://down.zhaomi.cn/web/assets/video/16_9_1.mp4",
          //       title : "video title",
          //       type : "file",
          //     })
          //   }
          // }

          // debug video
          // if(HelperCore.IS_LOCAL){
          //   event.attachments = event.attachments || [];
          //   if(!event.attachments.some(item => item.contentType == "audio")){
          //     event.attachments.unshift({
          //       contentType : "audio",
          //       content : "",
          //       url : "https://down.zhaomi.cn/web/assets/audio/dudu.mp3",
          //       title : "audio title",
          //       type : "file",
          //     })
          //   }
          // }

          this.replaceEvent(steps.list, event);

          const images = this.getImagesFromAttachments(attachments);
          if(images.length){
            previewImages = [...previewImages, ...images.map(image => ({
              image,
              event,
            }))];
          }
          break;
        }
        // 大纲
        case "planDataUpdate":{
          try{
            stepsList = JSON.parse(event.content);
          } catch(e){}
          break;
        }
      }
    });

    // 根据大纲节点，补全“待执行”节点
    if(stepsList?.length){
      let steps = list.find(item => item.type == "steps");
      const _list = steps.list;
      _list.forEach((level1Event, level1Index) => {
        const stepLevel1Event = stepsList.find(event => event.planStepId == level1Event.planStepId);
        if(stepLevel1Event){
          const stepLevel2Events = stepLevel1Event.sub_steps;
          if(!stepLevel2Events?.length){
            return;
          }
          const currentLevel2Event = level1Event.children?.length ? level1Event.children[level1Event.children.length - 1] : null;
          const currentLevel2Index = currentLevel2Event
            ? stepLevel2Events.findIndex(event => event.planStepId == currentLevel2Event.planStepId)
            : -1;
          if(currentLevel2Index != stepLevel2Events.length - 1){
            level1Event.children = level1Event.children || [];
            for(let i = currentLevel2Index + 1, len = stepLevel2Events.length; i < len; i++){
              const event = stepLevel2Events[i];
              level1Event.children.push({
                type : "subPlanStep",
                planStepId : event.planStepId,
                title : event.title,
                stepStatus : event.status,
              });
            }
          }
        }
      })

      const currentLevel1Index = lastLevel1Event
        ? stepsList.findIndex(event => event.planStepId == lastLevel1Event.planStepId)
        : -1;
      for(let i = currentLevel1Index + 1, len = stepsList.length; i < len; i++){
        const event = stepsList[i];
        const level1Event = {
          type : "planStep",
          planStepId : event.planStepId,
          title : event.title,
          stepStatus : "waiting",
          displayIndex : level1Events.length + (i - currentLevel1Index - 1),
        }
        _list.push(level1Event);

        level1Event.children = level1Event.children || [];
        (event.sub_steps || []).forEach(event => {
          level1Event.children.push({
            type : "subPlanStep",
            planStepId : event.planStepId,
            title : event.title,
            stepStatus : event.status,
          });
        })
      }

      // 将 agentStatus 置于底部
      steps.list = _list.sort((a, b) => {
        if(a.type == "agentStatus"){
          return 1;
        }
        if(b.type == "agentStatus"){
          return -1;
        }
        return 0;
      });
    }

    // 工作流，并行 MCP 聚类
    // TODO 自测效果
    // if(this.isAgentFlow){
    //   let steps = list.find(item => item.type == "steps");
    //   const _list = steps.list;
    //   if(_list?.length){
    //     _list.sort((a, b) => {
    //       const { idx: idA, timestamp: timestampA } = a;
    //       const { idx: idB, timestamp: timestampB } = b;
    //       if(!idA || !idB){
    //         return 0;
    //       }
    //       return idA > idB ? 1 : -1;
    //     })
    //   }
    // }

    // debug
    // HelperCore.log("getFixedEvents", events, list);

    this.previewImages = previewImages;
    return list;
  }

  /**
   * 格式化工具文案
   * @param {Object} event
   */
  getFixedToolText (event, needString) {
    if(!event){
      if(needString){
        return "";
      }

      return {};
    }

    const {
      title,
      status,
      description,
    } = event;

    const result = {
      eventStatus : status,
      status : "",
      title : title || "",
      content : title || description || "",
      done : status == "success",
    };

    const config = getMcpToolsConfig(event);
    if(config && config.getFixedToolText){
      Object.assign(result, config.getFixedToolText(event));
    }

    if(needString){
      return `${result.status} ${result.content}`;
    }

    return result;
  }

  /**
   * 切换步骤 ID
   * @param {String} currentPanelId
   */
  setCurrentPanelId (currentPanelId, byUser) {
    // 用户手选后，不再自动切换
    if(this.hasSelectedPanelId && !byUser && this.showRightPanel){
      return;
    }

    if(!currentPanelId && currentPanelId != null){
      console.error(`setCurrentPanelId id error:`, currentPanelId);
      return;
    }

    this.currentPanelId = currentPanelId;
    if(byUser){
      console.warn(`setCurrentPanelId byUser`);
      this.hasSelectedPanelId = true;
      this.setShowRightPanel(true);
    }
  }
  /**
   * @param {Boolean} hasSelectedPanelId
   */
  setHasSelectedPanelId (hasSelectedPanelId) {
    this.hasSelectedPanelId = hasSelectedPanelId;
  }

  setContinue() {
    runInAction(() => {
      this.hasSelectedPanelId = false;
      this.setCurrentPanelId(this.toolsNodes[this.toolsNodes.length - 1].planStepId);
    });
  }

  /**
   * @param {Boolean} hasAutoShowRightPanel
   */
  setHasAutoShowRightPanel (hasAutoShowRightPanel) {
    // debug
    // console.warn(`setHasAutoShowRightPanel`, hasAutoShowRightPanel);

    this.hasAutoShowRightPanel = hasAutoShowRightPanel;
  }

  /**
   * VncViewer 参数格式化
   * @param {Object} event
   */
  getFixedPanelParams (event) {
    if(!event){
      return {};
    }

    return this.getEventPanelParams(event);
  }
  getEventPanelParams (event){
    const {
      title,
      description,
      planStepId,
      msg_id,
    } = event;

    const result = {
      data : "",
      event,
      planStepId,
      msg_id,
    };

    const config = getMcpToolsConfig(event);
    if(config.getEventPanelParams){
      Object.assign(result, config.getEventPanelParams(event));
    } else {
      result.tool = "text";
      result.title = title || description;
      result.data = event.content;
    }

    return result;
  }

  /**
   * 右侧显示的文件节点
   * @param {Object} event
   */
  setCurrentFileNode (event) {
    // if(
    //   event
    //   && event.contentType != "pdf"
    //   && event.messageId
    //   && this.currentFileNode
    //   && event.messageId != this.currentFileNode.messageId
    // ){
    //   this.isHtmlPreview = false;
    // }

    this.currentFileNode = event;
    if(event && this.isAgent){
      this.setShowRightPanel(true);
    }
  }

  /**
   * Html 预览状态
   * @param {Boolean} isHtmlPreview
   */
  setHtmlPreview (isHtmlPreview) {
    this.isHtmlPreview = isHtmlPreview;
  }
  toggleHtmlPreview = () => {
    this.setHtmlPreview(!this.isHtmlPreview);
  }

  /**
   * 切换 Html 主题色
   * @param {String} htmlTheme dark|light
   */
  setHtmlTheme (htmlTheme) {
    this.htmlTheme = htmlTheme;
  }
  toggleHtmlTheme = () => {
    this.setHtmlTheme(this.htmlTheme == "dark" ? "light" : "dark");
  }

  getShareUrl () {
    return request.post("/share/gen", {
      cid : this.chatStore.chatId,
      type : 2,
    })
    .then(res => {
      const id = res.data.id;
      const from = client.isLLQ ? "pc" : (client.isClient ? "weishi" : "web");
      return HelperCore.repaireUrl(`${location.protocol}//${location.host}/share/mcp?id=${id}&from=${from}`);
    })
  }
  // FIXME: 先注释掉这块代码
  copyShareUrl () {
    // this.getShareUrl()
    // .then(url => {
    //   const copied = copy(url);
    //   copied && Other.toast('链接已复制', "success");
    // })
    // .catch(Loader.error);
  }

  /**
   * 保留节点的输入输出参数
   */
  mergeEvent (newEvent, oldEvent) {
    return {
      ...newEvent,
      ...{
        message : {
          ...oldEvent.message,
          ...newEvent.message,
        },
      },
    }
  }
  /**
   * 入参出参
   */
  getNodeParamsText (event) {
    if(!event){
      return "";
    }

    // 知识库
    if(event.type == "knowledgeStatus"){
      return `AI 重点:
    ${event.content}`;
    }

    // 输入输出
    if(!event.message){
      return "";
    }
    if(!event.message.tool_request && !event.message.tool_respond){
      return "";
    }

    const result = [];

    // 中台 flow
    if([
      "agentBlock",
    ].includes(event.type)){
      try{
        const toolParams = JSON.parse(event.message.tool_respond || event.message.tool_request);
        const { display_message, block_name, inputs, result:toolResult } = toolParams;

        // 入参，优先取 inputs
        if(inputs && Object.keys(inputs).length){
          result.push(`节点 ${block_name} 输入:\n`);
          result.push(JSON.stringify(inputs, null, "\t"));
          result.push("\n");
        }
        else if(display_message){
          const node = display_message[0];
          const nodeMsg = node.node_msg;
          result.push(`节点 ${block_name} 输入:\n`);
          result.push(JSON.stringify(nodeMsg, null, "\t"));
          result.push("\n");
        }

        // 出参，优先取 result
        if(event.status && event.status != "running"){
          if(toolResult && Object.keys(toolResult).length){
            result.push(`节点 ${block_name} 输出:\n`);
            result.push(JSON.stringify(toolResult, null, "\t"));
          }
          else if(display_message){
            const node = display_message[display_message.length - 1];
            const nodeMsg = node.node_msg;
            result.push(JSON.stringify(nodeMsg, null, "\t"));
          }
        }

        if(result.length){
          return result.join("\n");
        }
        return "";
      } catch(e){
        return "";
      }
    }

    if(event.message.tool_request){
      result.push(`MCP：${event.serverNameCN} TOOL：${event?.toolCN} 输入:\n`);
      try{
        result.push(JSON.stringify(JSON.parse(event.message.tool_request), null, '\t'))
      } catch(e){
        result.push(event.message.tool_request);
      }
      result.push("\n");
    }
    if(event.message.tool_respond){
      result.push(`MCP：${event.serverNameCN} TOOL：${event?.toolCN} 输出:\n`);
      try{
        result.push(JSON.stringify(JSON.parse(event.message.tool_respond), null, '\t'))
      } catch(e){
        result.push(event.message.tool_respond);
      }
    }
    return result.join("\n");
  }

  replaceEvent (list, event) {
    if(!event.planStepId){
      list.push(event);
      return;
    }
    const index = list.findIndex(item => item.planStepId == event.planStepId);
    if(index == -1){
      list.push(event);
    } else {
      list.splice(index, 1, event);
    }
  }

  /**
   * 无效 event，会被过滤
   * @param {Object} event
   */
  isInvalidEvent (event) {
    if(!event){
      return true;
    }
    const {
      tool,
      type,
      status,
    } = event;

    // 没有图片的 BU 截图
    if(tool == "browser_automation_task"){
      let url = "";
      try{
        url = event.detail.results[0].url;
      } catch(e){}
      if(!url){
        return true;
      }
    }

    if (tool == "gen_ppt_html") {
      // 只有在生成中的、生成成功的状态下，才显示
      if (status !== "running" && status !== "success") {
        return true; // 生成中
      }
    }

    // 大纲节点
    if(type == "planUpdate" && event.planStepId == "sub_step_data"){
      return true;
    }

    return false;
  }
  /**
   * 无效 event，直接抛弃
   * @param {Object} event
   */
  isFilteredEvent (event) {
    if(!event){
      return true;
    }

    const {
      tool,
      type,
      hideEvent,
    } = event;

    if(hideEvent){
      return true;
    }

    if(tool == "sandbox_coder"){
      // 非新版 UI 对应节点
      if(!event.new_ui){
        return true;
      }

      // 没有 language 的 sandbox_coder
      // let language = event.language;
      // if(!language){
      //   try{
      //     language = event.detail.results[0].language;
      //   } catch(e){}
      //   if(!language){
      //     return true;
      //   }
      // }
    }

    // 生图 generated_sandbox_coder 合并到 sandbox_coder 中
    if(tool == "generated_sandbox_coder"){
      return true;
    }

    // 没有 detail.result 的 knowledge_analyse
    // 垃圾数据，已反馈给天洲，模型修复后可删除本 case
    if(tool == "knowledge_analyse"){
      if(!event.detail){
        return true;
      }
    }

    // 不支持调起 BU 的环境
    if(tool == "bu_status" && event.buType == "action"){
      return !client.supportOpenBu;
    }

    // 为兼容老版本客户端，模型同时下发 chat、chat_summary
    if(type == "chat" && event.web_disable){
      return true;
    }

    // 工作流开始结束节点
    if(["input", "output"].includes(event.block_type)){
      return true;
    }

    // 打点用的，chat_log
    if (type === 'chat_log') {
      return true;
    }
    return false;
  }
  /**
   * 提取 event 中的图片
   */
  getImageFromEvent (event) {
    const panelParams = this.getEventPanelParams(event);
    if(panelParams.tool == "image" && panelParams.data){
      return {
        url : panelParams.data,
        title : panelParams.title || ""
      };
    }

    return null;
  }
  getImagesFromAttachments (attachments) {
    return (attachments || []).filter(item => item.contentType == "image" && item.url);
  }
  /**
   * 不同 event，回放时的停留时长
   */
  getReplyTime (event) {
    if(!event){
      return DEFAULT_REPLY_TIME;
    }

    const {
      tool,
      type,
    } = event;

    // BU 截图
    if(tool == "browser_automation_task"){
      return 600;
    }

    return DEFAULT_REPLY_TIME;
  }

  /**
    * 下载文件
    * @typedef {Object} file
    * @property {string} url - 文件url
    * @property {string} title - 文件名称
   */
  async downloadFile (file)  {
    const {url, title:fileName} = file
    if (/^https/.test(url)) {
      let _fileName = fileName;
      // 如果fileName没有文件后缀，则从url里取
      if (!/(\.[a-z]{1,4})(?:$|\?)/gi.test(_fileName) && /(\.[a-z]{1,4})(?:$|\?)/gi.test(url)) {
        _fileName = fileName + RegExp.$1;
      }
      if (/qiniu/.test(url)) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        if (!params.get('attname')) {
          params.set('attname', encodeURIComponent(_fileName))
        }
        HelperCore.saveFile(urlObj.href, _fileName)
      }
      else if (/xstore/.test(url)) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        if (!params.get('response-content-disposition')) {
          params.set('response-content-disposition', 'attname');
          params.set('filename', encodeURIComponent(_fileName))
        }
        HelperCore.saveFile(urlObj.href, _fileName)
      }
       else {
        if(HelperCore.isWeixin()){
          HelperCore.saveFile(url, _fileName);
        }else{
          const blob = await fetch(url).then(res => res.blob()).catch(err => null);
          if (blob) {
            const url = URL.createObjectURL(blob);
            HelperCore.saveFile(url, _fileName);
          }
        }
      }
    }
  }


  /**
   * 智能体对话的前置限制条件
   * 需求:https://fvekd5tqqq.feishu.cn/docx/URmudg1xHowVLixFQf6cyzOln1H
   *  */
  async checkMcpCondition({ params, callback = () => {} }) {
    const { agentId = this.agentId, aiAgentId } = params;

    // 检查是否为智能体
    if (!this.isAgent || !this.isMcpAgent) {
      return await callback(params);
    }

    // 是否隐藏邀请码弹窗
    const hideInviteCode =
    globalStore.cloudConfig?.assistantMcpServerConfig?.hide_invite_code == '1';
    if(!hideInviteCode) {
      // 检查是否为限免智能体且未激活
      const id = aiAgentId || agentId;
      if (globalStore.isLimitedAgent(id)) {
        const inviteCodeStatus = await globalStore.getInviteStaus();
        if (inviteCodeStatus !== INVITE_STATUS_ACTIVE) {
          return InviteCodeModal.show({
            callback: async () => {
              this.checkMcpCondition({ params, callback });
            },
          });
        }
      }
    }

    // 检查是否配置本地MCP服务 is_local:1为本地服务 is_local:2为云端服务
    const hasLocalServer = this.agentInfo.server_list?.some(
      sever => sever.is_local === 1
    );
    if (hasLocalServer && !client.supportMcpAgent) {
      if (client.isLLQ || client.isSeLLQ) {
        return Other.toast('请升级至最新版体验该功能', 'warning');
      } else {
        setTimeout(() => {
          client.openPage('/download');
        }, 2 * 1000);
        return Other.toast('去PC端体验完整功能', 'warning');
      }
    }

    // 所有满足条件，执行发消息
    return await callback(params);
  }


}

export default McpStore;
